import { Dialog, IconButton, Typography } from '@material-ui/core';
import { fade, makeStyles, Theme } from '@material-ui/core/styles';
import { Check, Close, InfoOutlined } from '@material-ui/icons';
import { Button } from '@roc/ui';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { ACH_GUIDE_STEPS } from './achConnectionGuideSteps';
import { AchGuideVariant } from './achGuideStorage';

const useStyles = makeStyles((theme: Theme) => ({
  paper: { maxWidth: 780, overflow: 'hidden' },
  layout: { display: 'flex', minHeight: 0 },
  rail: {
    flex: '0 0 190px',
    background: theme.palette.action.hover,
    padding: theme.spacing(2, 1),
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  railTitle: { padding: theme.spacing(0, 1), marginBottom: theme.spacing(1) },
  railItem: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
    width: '100%',
    textAlign: 'left',
    border: 0,
    background: 'transparent',
    padding: theme.spacing(0.75, 1),
    borderRadius: 8,
    cursor: 'pointer',
    font: 'inherit',
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
    '&:hover': { background: theme.palette.action.selected },
  },
  railActive: {
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontWeight: 600,
    '&:hover': { background: theme.palette.background.paper },
  },
  dot: {
    flex: '0 0 18px',
    height: 18,
    borderRadius: '50%',
    border: `1px solid ${theme.palette.text.disabled}`,
    fontSize: 11,
    fontWeight: 400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotActive: {
    background: theme.palette.text.primary,
    color: theme.palette.background.paper,
    borderColor: theme.palette.text.primary,
  },
  dotDone: { background: theme.palette.success.main, color: theme.palette.success.contrastText, borderColor: theme.palette.success.main },
  main: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(2, 3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: theme.spacing(1) },
  headerEnd: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  segments: { display: 'flex', gap: 4 },
  segment: { flex: 1, height: 4, borderRadius: 2, background: theme.palette.divider },
  segmentOn: { background: theme.palette.primary.main },
  art: {
    background: theme.palette.action.hover,
    borderRadius: 8,
    height: 252,
    padding: theme.spacing(1.5),
    boxSizing: 'border-box',
  },
  tip: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
    background: fade(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.dark,
    borderRadius: 8,
    padding: theme.spacing(1, 1.5),
  },
  tipIcon: { fontSize: 18, flex: '0 0 auto', marginTop: 1 },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: theme.spacing(1) },
  footerButtons: { display: 'flex', gap: theme.spacing(1) },
}));

export type AchConnectionGuideProps = {
  /** 'staff': the full guide for the ACH Consent tab. 'borrower': the shorter Plaid-only guide for the public ACH page. */
  variant: AchGuideVariant;
  open: boolean;
  /** Called on any way of closing the guide (close button, Esc, Got it). */
  onClose: () => void;
};

/**
 * Step-by-step guide to the Plaid bank connection of the ACH form. Shown to staff on the ACH Consent tab (the whole
 * path, including creating the link and the manual-link fallback) and to the borrower on the public ACH page (just the
 * Plaid steps and what to do if the bank can't be connected). Content lives in achConnectionGuideSteps.tsx.
 */
export const AchConnectionGuide = ({ variant, open, onClose }: AchConnectionGuideProps) => {
  const classes = useStyles();
  const steps = ACH_GUIDE_STEPS[variant];
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setStepIndex(0);
    }
  }, [open]);

  const last = stepIndex === steps.length - 1;
  const step = steps[stepIndex];
  const Art = step.Art;

  const close = () => onClose();
  const next = () => (last ? close() : setStepIndex(stepIndex + 1));
  const back = () => setStepIndex(Math.max(0, stepIndex - 1));

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowRight' && !last) {
      setStepIndex(stepIndex + 1);
    } else if (event.key === 'ArrowLeft') {
      back();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      onKeyDown={onKeyDown}
      fullWidth
      maxWidth="md"
      classes={{ paper: classes.paper }}
      aria-labelledby="ach-guide-title"
    >
      <div className={classes.layout}>
        <nav className={classes.rail} aria-label="Guide steps">
          <Typography variant="caption" color="textSecondary" component="p" className={classes.railTitle}>
            How it works
          </Typography>
          {steps.map((s, index) => (
            <button
              key={s.id}
              type="button"
              data-testid={`achGuideStep-${s.id}`}
              aria-current={index === stepIndex ? 'step' : undefined}
              className={clsx(classes.railItem, index === stepIndex && classes.railActive)}
              onClick={() => setStepIndex(index)}
            >
              <span
                className={clsx(
                  classes.dot,
                  index === stepIndex && classes.dotActive,
                  index < stepIndex && classes.dotDone
                )}
              >
                {index < stepIndex ? <Check style={{ fontSize: 12 }} /> : index + 1}
              </span>
              <span>{s.title}</span>
            </button>
          ))}
        </nav>

        <div className={classes.main}>
          <div className={classes.header}>
            <div>
              <Typography variant="caption" color="textSecondary">
                Step {stepIndex + 1} of {steps.length}
              </Typography>
              <Typography variant="h6" id="ach-guide-title">
                {step.title}
              </Typography>
            </div>
            <div className={classes.headerEnd}>
              <IconButton size="small" aria-label="Close guide" data-testid="achGuideClose" onClick={close}>
                <Close fontSize="small" />
              </IconButton>
            </div>
          </div>

          <div className={classes.segments} aria-hidden="true">
            {steps.map((s, index) => (
              <div key={s.id} className={clsx(classes.segment, index <= stepIndex && classes.segmentOn)} />
            ))}
          </div>

          <div className={classes.art}>
            <Art />
          </div>

          <Typography variant="body2">{step.body}</Typography>

          <div className={classes.tip}>
            <InfoOutlined className={classes.tipIcon} />
            <Typography variant="body2">{step.tip}</Typography>
          </div>

          <div className={classes.footer}>
            <div className={classes.footerButtons}>
              {stepIndex > 0 && (
                <Button testId="achGuideBack" variant="outlined" onClick={back}>
                  Back
                </Button>
              )}
              <Button testId="achGuideNext" variant="contained" color="primary" onClick={next}>
                {last ? 'Got it' : 'Next'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
